import { useMemo } from 'react';

import { DEFAULT_SLOT_KEY, SlotsSelections } from 'features/menu/components/product-wizard/types';
import { computeMenuItemPrice } from 'features/menu/components/product-wizard/utils';
import { Reward } from 'features/reward/types';

import { EntryTypeEnum, IPriceDto } from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { sumAppliedTimesApplied } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { useUIContext } from 'state/ui';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getComboSlotItemPrice, getFirstDefaultId, isItem } from 'state/unified-menu/utils';

export const useProductTotalPrice = ({
  id,
  selectedProduct,
  slotsSelections,
  productQuantity,
  cartIdEditing,
  rewardId,
  reward,
}: {
  id: string;
  selectedProduct: ILocalizedMenuEntry;
  slotsSelections: SlotsSelections;
  productQuantity: number;
  cartIdEditing?: string;
  rewardId?: string;
  reward?: Pick<Reward, 'pointCost'>;
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { posServiceMode } = useMenuQueryOptions();
  const { indexedMenu, indexedRewards } = useUnifiedMenuContext();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );
  const availablePoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();

  const discountPrice = useMemo(() => {
    const individualItemPrice = selectedProduct?.price?.[posServiceMode] ?? 0;
    if (appliedLoyaltyRewards[cartIdEditing ?? '']) {
      return individualItemPrice;
    }

    let availableRewardSlots = Math.max(
      0,
      rewardRedemptionLimit - sumAppliedTimesApplied(appliedLoyaltyRewards)
    );
    let effectiveAvailablePoints = availablePoints;

    // When the cart is at the reward cap and we are adding a new reward (not editing), the add
    // flow will evict the oldest applied reward to free one slot. Reserve that slot here so the
    // price preview shows the discounted price rather than the full item price.
    if (enableRewardUnstacking && rewardId && !cartIdEditing && availableRewardSlots === 0) {
      const evictedKey = Object.keys(appliedLoyaltyRewards)[0];
      const evicted = evictedKey ? appliedLoyaltyRewards[evictedKey] : undefined;
      if (evicted) {
        availableRewardSlots = 1;
        effectiveAvailablePoints = availablePoints + evicted.pointCost;
      }
    }

    const productQuantityForDiscount = enableRewardUnstacking
      ? Math.min(availableRewardSlots, productQuantity)
      : productQuantity;

    const menuReward = rewardId ? indexedRewards.find(rewardId) : undefined;
    const loyaltyReward = reward ?? availableLoyaltyRewardsMap[id];
    if (menuReward && loyaltyReward) {
      const maxApplicationsByPoints = Math.floor(
        effectiveAvailablePoints / loyaltyReward.pointCost
      );
      const timesApplied = Math.min(maxApplicationsByPoints, productQuantityForDiscount);
      return individualItemPrice * timesApplied;
    }

    return 0;
  }, [
    selectedProduct?.price,
    posServiceMode,
    appliedLoyaltyRewards,
    cartIdEditing,
    enableRewardUnstacking,
    rewardRedemptionLimit,
    productQuantity,
    indexedRewards,
    rewardId,
    reward,
    availableLoyaltyRewardsMap,
    id,
    availablePoints,
  ]);

  const totalPrice = useMemo(() => {
    return (
      computeTotalPrice(indexedMenu, posServiceMode, selectedProduct, slotsSelections) *
        productQuantity -
      discountPrice
    );
  }, [
    indexedMenu,
    posServiceMode,
    selectedProduct,
    slotsSelections,
    productQuantity,
    discountPrice,
  ]);

  const formattedTotalPrice = useMemo(() => formatCurrencyForLocale(totalPrice), [
    formatCurrencyForLocale,
    totalPrice,
  ]);

  return {
    totalPrice,
    formattedTotalPrice,
  };
};

export function computeTotalPrice(
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>,
  posServiceMode: PosServiceMode,
  selectedProduct: ILocalizedMenuEntry,
  slotsSelections: SlotsSelections
): number {
  if (isItem(selectedProduct)) {
    const modifierSelections = slotsSelections[DEFAULT_SLOT_KEY]?.modifiers ?? {};
    return computeMenuItemPrice(
      indexedMenu,
      selectedProduct,
      posServiceMode,
      undefined,
      modifierSelections
    );
  }

  let comboPrice = selectedProduct?.price?.[posServiceMode] ?? 0;
  for (const comboSlotRef of selectedProduct?.options?.entries ?? []) {
    const comboSlot = indexedMenu.get(comboSlotRef.entryId);
    const defaultComboSlotPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, undefined);

    let item: ILocalizedMenuEntry;
    let contextualPrice: IPriceDto | undefined;
    const slotSelection = slotsSelections[comboSlot.id];
    if (slotSelection?.itemId) {
      item = indexedMenu.get(slotSelection.itemId, { type: EntryTypeEnum.ITEM });
      contextualPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, slotSelection.itemId);
    } else {
      item = indexedMenu.get(getFirstDefaultId(comboSlot));
      contextualPrice = defaultComboSlotPrice;
    }

    const modifierSelections = slotSelection?.modifiers ?? {};
    const itemPrice = computeMenuItemPrice(
      indexedMenu,
      item,
      posServiceMode,
      contextualPrice,
      modifierSelections
    );

    // Only add price for upsell combo slots or customizations
    const defaultPrice = defaultComboSlotPrice?.[posServiceMode] ?? 0;
    if (itemPrice !== defaultPrice) {
      comboPrice += itemPrice;
    }
  }
  return comboPrice;
}
